/**
 * Core type definitions for Atlas Restoration application
 */

/**
 * User entity representing an employee or system user
 */
export interface User {
  id: number;
  userName: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  isActive: boolean;
  roles: string[];
  permissions: UserPermissions;
  createdAt: string;
  updatedAt: string;
}

/**
 * Permission string constants matching backend Permissions.cs
 */
export const Permissions = {
  // Leads
  LeadsCreate: 'leads:create',
  LeadsRead: 'leads:read',
  LeadsUpdate: 'leads:update',
  LeadsDelete: 'leads:delete',
  LeadsAssign: 'leads:assign',

  // Jobs
  JobsRead: 'jobs:read',
  JobsUpdate: 'jobs:update',
  JobsCost: 'jobs:cost',

  // Reports
  ReportsJobCost: 'reports:job-cost',
  ReportsEstimator: 'reports:estimator',
  ReportsLeadSheet: 'reports:lead-sheet',

  // Users
  UsersRead: 'users:read',
  UsersCreate: 'users:create',
  UsersUpdate: 'users:update',

  // Settings
  SettingsRead: 'settings:read',
  SettingsUpdate: 'settings:update',

  // Subcontractors
  SubcontractorsRead: 'subcontractors:read',
  SubcontractorsCreate: 'subcontractors:create',
  SubcontractorsUpdate: 'subcontractors:update',

  // Insurance
  InsuranceRead: 'insurance:read',
  InsuranceCreate: 'insurance:create',
  InsuranceUpdate: 'insurance:update',

  // Commissions
  CommissionsRead: 'commissions:read',
  CommissionsUpdate: 'commissions:update',

  // Wildcard (grants all permissions)
  All: '*',
} as const;

export type Permission = (typeof Permissions)[keyof typeof Permissions];

/**
 * User permissions - raw array of permission strings from JWT
 */
export type UserPermissions = string[];

/**
 * Authentication DTO from the API
 */
export interface AuthResponseDto {
  token: string;
  userId: number;
  username: string;
  email: string;
  firstName: string;
  lastName: string;
  roleId: number;
  roleName: string;
  permissions: string[];
}

/**
 * Result wrapper for API responses
 */
export interface Result<T> {
  success: boolean;
  data: T | null;
  errors: string[] | null;
}

/**
 * Authentication response from login endpoint
 */
export interface AuthResponse {
  token: string;
  user: User;
  expiresAt: string;
}

/**
 * Login credentials (API expects UsernameOrEmail with capital letters)
 */
export interface LoginCredentials {
  UsernameOrEmail: string;
  Password: string;
}

/**
 * Registration credentials (API expects capital letters)
 */
export interface RegisterCredentials {
  Username: string;
  Email: string;
  Password: string;
  FirstName: string;
  LastName: string;
  RoleId?: number;
}

/**
 * API error response structure
 */
export interface ApiError {
  message: string;
  errors?: Record<string, string[]>;
  statusCode: number;
}

/**
 * Generic API response wrapper
 */
export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

/**
 * Pagination metadata
 */
export interface PaginationMeta {
  currentPage: number;
  pageSize: number;
  totalPages: number;
  totalCount: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

/**
 * Paginated response
 */
export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationMeta;
}
